/**
 * Every user-facing string of the "Set up Rock" flow (UI-INT brief §4), in
 * plain language. This is the one home for them: the page, the Rock card,
 * Getting started, the settings dialog and the tests all import from here,
 * so a wording change happens once.
 *
 * Vocabulary rule: never show adopt, wire, realization, partition,
 * dimension, binding, pipeline or setup run to people.
 *
 * Later tasks append their own strings in their own delimited section
 * (`// --- B ---`, `// --- C ---`, `// --- D ---`) to keep merges clean.
 */

// --- A: page shell ---------------------------------------------------------

/** The board page's title: the Rock card's "Manage measurements" lands here; guided setup is `/setup/rock/:id`. */
export const SETUP_ROCK_TITLE = 'Manage Rock measurements'
export { OPEN_INTEGRATIONS } from '../../../../copy/common.js'
export const REFRESH_FROM_ROCK = 'Refresh from Rock'
export const RAIL_LABEL = 'Rock setup steps'
export const RAIL_PROGRESS_LABEL = 'Your progress'
export const RAIL_JUMP_LABEL = 'Jump to a setup step'

/** @param {string} stepLabel */
export const nextStepCta = (stepLabel) => `Next: ${stepLabel}`

/** Rail labels, in order (brief §1 table). */
export const STEP_LABELS = {
  connect: 'Connect Rock',
  match: 'Match campuses & services',
  choose: 'Choose measurements',
  activity: 'Setting up',
  first_values: 'Check first values',
}

/** One line under each step title in its panel. */
export const STEP_INTROS = {
  connect: 'Graphit talks to your Rock through the Graphit plugin. Here is what it can do today.',
  match: 'Tell Graphit which of its campuses and service times are the same as Rock’s, so every number lands in the right place.',
  choose: 'Pick the measurements Graphit should read from your Rock, or have Graphit create them in Rock for you.',
  activity: 'Everything you started from this page, and how it is going.',
  first_values: 'Once Rock has computed a measurement, check its first numbers look right.',
}

/** The panel footer's "What happens next" sentence per step (X1). */
export const WHAT_HAPPENS_NEXT_LABEL = 'What happens next'
export const WHAT_HAPPENS_NEXT = {
  connect: 'Next you match your campuses and service times, so Rock’s numbers land on the right campus.',
  match: 'Next you choose which measurements to bring in. Anything that needs a match you skip here will ask for it then.',
  choose: 'Graphit sets up what you pick (about a minute), imports past values, then asks you to check the first numbers.',
  activity: 'When a measurement is ready, Rock fills it in on its own schedule and Graphit asks you to check the first values.',
  first_values: 'Once the numbers look right, Graphit keeps them in sync from Rock every hour.',
}

/** Rail sentences for a step that blocks others, or needs you. */
export const STEP_NOTES = {
  needsRelink: 'Rock stopped accepting Graphit. Reconnect to continue.',
  matchCampusesFirst: 'Match your campuses first so numbers land on the right campus.',
  matchServicesFirst: 'Match your service times first so services line up.',
  running: 'Graphit is setting things up now.',
  pluginTooOld: 'Update the Graphit plugin in your Rock to continue.',
  notConnected: 'Rock isn’t connected yet.',
  cannotCreate: 'Graphit can’t create metrics in your Rock yet.',
  waitingOnRock: 'Rock hasn’t computed these yet. First values show up here once it has.',
  checkValues: (count) => `${count} ${count === 1 ? 'measurement is' : 'measurements are'} waiting for your check.`,
}

export const RECONNECT_TO_CONTINUE = 'Reconnect Rock to continue'
export const RECONNECT_TO_CONTINUE_MESSAGE = 'Graphit can’t reach this Rock with its current sign-in. Reconnect it on step 1, then come back here.'
export const GO_TO_STEP_1 = 'Go to step 1'

// Board load states (§6, ER1)
export const BOARD_ERROR_TITLE = 'Couldn’t reach your Rock'
export const BOARD_ERROR_MESSAGE = 'Check it’s online, then try again.'
/** @param {string} [name] */
export const contactingRock = (name) => `Contacting ${name || 'Rock'}… this can take up to 30 seconds.`

// --- A: step 1, Connect Rock ------------------------------------------------

export const CONNECTION_FACTS = {
  name: 'Name',
  server: 'Rock server',
  pluginVersion: 'Graphit plugin version',
  canCreate: 'Graphit can create metrics in your Rock',
  yes: 'Yes',
  no: 'No',
  unknown: 'Unknown',
}
export const RECONNECT_ROCK = 'Reconnect Rock'
export const ALLOW_CREATING_METRICS = 'Allow creating metrics'
export const REDIRECTING = 'Redirecting…'
export const CANNOT_CREATE_YET = 'Graphit can’t create metrics in your Rock yet'
export const CANNOT_CREATE_YET_DETAIL =
  'You can still bring in metrics Rock already has. To let Graphit create new ones, allow it in Rock.'
export const CONNECTED_LINE = 'Rock is connected and Graphit can read its metrics.'
export const NOT_CONNECTED_LINE = 'Rock isn’t connected yet. Connect it to continue.'
export const PLUGIN_TOO_OLD_TITLE = 'Update the Graphit plugin in your Rock'
/** @param {string} minVersion */
export const pluginTooOldMessage = (minVersion) =>
  `Setting up measurements needs version ${minVersion} or newer. Install the latest release in Rock, then choose Check again.`
/** E11: re-reads the plugin's capabilities (a page refresh re-read nothing). */
export const CHECK_PLUGIN_AGAIN = 'Check again'
export const CHECKING_PLUGIN = 'Checking…'
export const PLUGIN_STILL_OLD = 'Rock still reports the older plugin. Finish the update in Rock, then check again.'
export const PLUGIN_CHECK_FAILED = 'Couldn’t reach Rock to check the plugin. Try again in a moment.'
export const UPDATE_PLUGIN_HOW = 'How to update the plugin'
export const NO_AUTHORIZATION_URL = 'No authorization URL was returned.'
export const AUTHORIZATION_FAILED = 'Failed to start authorization.'

// --- A: step 2 stub (D replaces) ---------------------------------------------

export const MATCH_CAMPUSES = 'Match campuses'
export const MATCH_SERVICE_TIMES = 'Match service times'
export const MORE_MATCHES = 'More matches (optional)'
export const CAMPUSES_MATCHED = 'Every Rock campus has a Graphit match.'
export const CAMPUSES_NEED_MATCH = 'Some Rock campuses still need a Graphit match.'
export const SERVICES_MATCHED = 'Service times are matched, or your Rock has none to match.'
export const SERVICES_NEED_MATCH = 'Some Rock service times still need a Graphit match.'
export const YOUR_ROCK_SERVICE_TIMES = 'Your Rock service times'

// --- A: step 3 stub (B replaces) ----------------------------------------------

/**
 * The step-3 intro that replaces the old inventory sentence (BD7).
 *
 * @param {{metrics: number, found: number, creatable: number}} counts
 * @returns {string}
 */
export function chooseIntro({ metrics, found, creatable }) {
  const metricWord = metrics === 1 ? 'metric' : 'metrics'
  const matchWord = found === 1 ? 'matches' : 'match'
  return `We found ${metrics} ${metricWord} in your Rock. ${found} ${matchWord} Graphit’s standard measurements; ${creatable} more can be created for you.`
}
export const YOUR_MEASUREMENTS = 'Your measurements'
export const NO_MEASUREMENTS_TITLE = 'Your Rock doesn’t have what Graphit’s standard measurements need yet'
export const NO_MEASUREMENTS_MESSAGE = 'You can still bring in other metrics your Rock already has, below.'

// --- A: step 4 stub (C replaces) ----------------------------------------------

export const ACTIVITY_RUNNING = 'Setting up'
export const ACTIVITY_READY = 'Ready'
export const ACTIVITY_NEEDS_ATTENTION = 'Needs attention'
export const ACTIVITY_RUNNING_LINE = 'Graphit is setting up your measurements. This page updates when it finishes.'
export const VIEW_PROGRESS = 'View progress'
export const ACTIVITY_EMPTY_TITLE = 'Nothing is setting up right now'
export const ACTIVITY_EMPTY_MESSAGE = 'When you connect or create a measurement, its progress shows here.'

// --- A: step 5, Check first values --------------------------------------------

export const LOOKS_RIGHT = 'Looks right'
export const SOMETHINGS_OFF = 'Something’s off'
export const FIRST_VALUES_ALL_CHECKED = 'Every measurement’s first values are checked.'
export const FIRST_VALUES_NONE_TITLE = 'Nothing to check yet'
export const FIRST_VALUES_NONE_MESSAGE = 'Set up a measurement first. Its first values show up here once Rock has computed them.'
export const WAITING_ON_ROCK_LABEL = 'Waiting on Rock'
/** @param {string} [scheduleSummary] - Rock's own "Weekly: Mondays at 4:00 AM" */
export function rockComputesOn(scheduleSummary) {
  if (!scheduleSummary) return 'Rock hasn’t computed this yet. We’ll show the first values here once it has.'
  return `Rock computes this on its own schedule (${scheduleSummary}). We’ll show the first values here.`
}

// --- A: entry points ---------------------------------------------------------

/** Rock card primary button (IN1). */
export const SET_UP_ROCK = 'Set up Rock'
/**
 * @param {number} done
 * @param {number} total
 */
export const continueSetup = (done, total) => `Continue setup · ${done} of ${total}`
export const CONTINUE_SETUP = 'Continue setup'
export const SYNC_NOW = 'Sync now'
export const SETTINGS = 'Settings'
/** Planning Center card button + dialog (PC1). */
export const CHOOSE_WHAT_TO_SYNC = 'Choose what to sync'
export const PCO_SOURCES_DESCRIPTION = 'Choose which Planning Center counts to bring into Graphit.'
/** OAuth hand-off toast (IN2). */
export const ROCK_CONNECTED_TOAST = 'Rock is connected. Let’s set it up.'

/** Getting started, "Connect your data" step when Rock is linked (GS2). */
export const CONTINUE_ROCK_SETUP = 'Continue Rock setup'
/**
 * @param {number} done
 * @param {number} total
 */
export const rockConnectedStatus = (done, total) =>
  `Rock connected · ${done} of ${total} measurements set up`
export const ROCK_CONNECTED = 'Rock connected'

// --- A: Rock settings dialog (IN4) ---------------------------------------------

/** @param {string} providerName */
export const settingsTitle = (providerName) => `${providerName} settings`
/** @param {string} providerName */
export const settingsDescription = (providerName) => `Your ${providerName} connection and whether it syncs.`
export const STATUS_LABEL = 'Status'
export const LAST_CHECK_LABEL = 'Last check'
export const LAST_CHECK_OK = 'Working'
export const LAST_CHECK_UNKNOWN = 'Not checked yet'
/** @param {string} [message] */
export const lastCheckFailed = (message) => (message ? `Last check failed: ${message}` : 'Last check failed')
export const CHECK_AGAIN = 'Check again'
export const CHECK_CONNECTION = 'Check connection'
export const CHECKING = 'Checking…'
export const SYNCING_LABEL = 'Syncing'
/** @param {number} count */
export const measurementsSyncing = (count) => `${count} ${count === 1 ? 'measurement' : 'measurements'} syncing`
export const SERVER_URL_LABEL = 'Server URL'
export const CONNECTION_NAME_LABEL = 'Connection name'
export const SYNC_ENABLED_LABEL = 'Sync enabled'

// --- A: shared §4 vocabulary for the step bodies (B, C and D apply these) ---

export const BADGES = {
  inYourRock: 'In your Rock',
  setUp: 'Set up',
  notMatchedYet: 'Not matched yet',
  cantBringInYet: 'Can’t bring in yet',
}
export const NOT_IN_ROCK_YET = 'Not in your Rock yet — we can create it'
export const BRING_INTO_GRAPHIT = 'Bring into Graphit'
export const CREATE_IN_ROCK = 'Create in Rock'
export const CREATE_NEW_IN_ROCK_INSTEAD = 'Create a new one in Rock instead'
export const CONNECT = 'Connect'
export const OPEN_METRIC = 'Open metric'
export const UNLINK = 'Unlink'
/** @param {string} label - Rock's own label for the split */
export const cantSplitBy = (label) => `Graphit can’t split numbers by ${label} yet.`
export const NOT_SPLIT = 'Not split'
export const OTHER_SPLIT = 'Other split'
/** @param {string} label */
export const newValuesAdded = (label) => `New ${label} values will be added automatically`
/** @param {string} label - e.g. "campuses" */
export const matchFirst = (label) => `Match ${label} first`
export const SERVICE_TIME = 'Service time'
export const MATCH_COLUMNS = {
  graphitMatch: 'Graphit match',
  dontMatch: 'Don’t match',
  addAsNew: 'Add as new',
  addAsNewCampus: 'Add as new campus',
}
export const RECORD_VALUES = 'Record values'
export const RECORD_VALUES_HINT = 'e.g. Ethnicity, Marital status'
export const QUESTION_BY_KIND = {
  locations: 'Which rooms?',
  interaction_channels: 'Which online channels?',
  connection_statuses: 'Which connection statuses?',
}
/** @param {number} count */
export const allCampuses = (count) => `All campuses (${count})`
export const CHANGE = 'Change'
export const MARK_SERVICE_TIMES_HINT = 'Mark service times in Rock, then refresh'
export const FOLDER_IN_ROCK = 'Folder in Rock'
export const FOLDER_IN_GRAPHIT = 'Folder in Graphit'
export const WHERE_ITS_FILED = 'Where it’s filed'
/** @param {number} count */
export const createMetricsInRock = (count) => `Create ${count} ${count === 1 ? 'metric' : 'metrics'} in Rock`
/** @param {string} title */
export const connectMeasurement = (title) => `Connect ${title}`
export const SETUP_FAILED_PLUGIN =
  'Rock couldn’t create this metric. Try again; if it keeps failing, check the Graphit plugin is up to date.'
export const RETRY = 'Retry'
export const FILLED_IN_BY_ROCK = 'Filled in by Rock'
/** @param {number} count */
export const hasValuesFilter = (count) => `Has values (${count})`
/** @param {number} count */
export const allFilter = (count) => `All (${count})`
/** @param {number} count */
export const cantBringInFilter = (count) => `Can’t bring in (${count})`
export const OTHER_METRICS_IN_ROCK = 'Other metrics in your Rock'
export const BROWSE_OTHER_METRICS = 'Browse other metrics in your Rock'
export const GO_TO_STEP_2 = 'Go to step 2'
export const EMPTY_SERVICES_SUCCESS = 'Your Rock has no service times to match — nothing to do here'
export const ROCK_NOT_COMPUTED_WARNING = 'Rock hasn’t computed this yet — you’ll get an empty metric until it does'

// --- A: Rock consent page (CO1-CO3) --------------------------------------------
// Applied by the consent page's owner (RockLinkConsentPage.jsx was owned by
// another task while UI-INT-A ran).

export const CONSENT_TITLE = 'Connect Rock to Graphit'
export const CONSENT_PRIMARY = 'Allow and connect'
export const CONSENT_SCOPE =
  'Graphit will read your Rock metrics, campuses, service times and group types, and create metrics in Rock only when you ask it to. You can disconnect from either side at any time.'
export const CONSENT_NEXT = 'Next: you’ll go back to Rock, then Graphit shows what it found.'

// --- First run (2026-09-25 feedback): pending banner, match all, bulk add ----

export const ROCK_SETUP_PENDING_MESSAGE = 'Rock is linked but not set up yet.'
/** The banner's landmark name: short, the message itself is read inside it. */
export const ROCK_SETUP_BANNER_REGION = 'Rock setup'
export const FINISH_SETUP = 'Finish setup'
/** The Rock card's action once guided setup is done: the board. */
export const MANAGE_MEASUREMENTS = 'Manage measurements'
export const ROCK_SETUP_PENDING_INDICATOR = 'Rock setup isn’t finished'

export const MATCH_ALL_CAMPUSES = 'Match all campuses'
export const MATCHING_CAMPUSES = 'Matching campuses…'
export const MATCH_ALL_CAMPUSES_HINT =
  'Graphit matches Rock campuses to yours by name and adds any it doesn’t have yet. Closed campuses are left out.'
export const CONFIGURE_MANUALLY = 'Configure manually'
export const MATCH_ALL_FAILED = 'Couldn’t match your campuses. Try again, or configure them manually.'
/**
 * "3 matched · 1 added · 1 left out (closed)" after "Match all campuses".
 *
 * @param {{matched?: number, created?: number, skipped?: number, inactive?: number}} summary
 * @returns {string}
 */
export const matchAllSummary = ({ matched = 0, created = 0, skipped = 0, inactive = 0 } = {}) =>
  [
    `${matched} matched`,
    `${created} added`,
    skipped > 0 ? `${skipped} need a manual match` : null,
    inactive > 0 ? `${inactive} left out (closed)` : null,
  ]
    .filter(Boolean)
    .join(' · ')
export const SKIPPED_CAMPUSES_HINT =
  'Some Rock campuses share a name with a Graphit campus that’s already taken, so Graphit didn’t guess. Match them manually.'

export const STANDARD_MEASUREMENTS = 'Graphit’s standard measurements'
export const ALREADY_SET_UP = 'Set up'
/** A row whose Rock metric was skipped on the guided setup's Discover. */
export const ROW_SKIPPED = 'Skipped'
export const SELECT_ALL = 'Select all'
/** @param {string} group */
export const selectAllIn = (group) => `Select all in ${group}`
/** @param {number} count */
export const addSelected = (count) => (count > 0 ? `Add ${count} selected` : 'Add selected')
export const NOTHING_SELECTED_HINT = 'Tick the measurements you want first.'
/** @param {string} rockTitle */
export const readsFromRock = (rockTitle) => `Reads Rock’s “${rockTitle}”`
/** @param {string} rockTitle - a Rock metric Graphit already reads with no standard key */
export const usesRockMetricInGraphit = (rockTitle) => `Uses Rock’s “${rockTitle}”, already in Graphit`
export const EDIT = 'Edit'
export const ANSWER_QUESTIONS = 'Answer questions'
export const DONE_EDITING = 'Done'
export const NAME_IN_GRAPHIT = 'Name in Graphit'
export const FOLDER_DEFAULT = 'Default folder'
export const CREATE_IN_ROCK_INSTEAD = 'Create a new one in Rock instead'
export const USE_ROCKS_METRIC = 'Use the one already in your Rock'
export const MATCH_SERVICE_TIMES_FIRST_ROW = 'Match service times first'
export const CREATES_IN_ROCK = 'Not in your Rock yet — Graphit creates it there for you'
export const MATCH_CAMPUSES_FIRST_ROW = 'Match campuses first'
export const CANT_BRING_IN = 'Can’t bring in yet'
/** Why a row can't be brought in, when the server gave no reason of its own. */
export const CANT_BRING_IN_WHY = {
  missingDimension: 'Your Rock connection can’t split numbers the way this measurement needs yet.',
  notInRockNoWrite: 'Not in your Rock, and Graphit can’t create metrics there yet.',
  unsupportedTemplate: 'Graphit can’t read this measurement from your Rock yet.',
  unsupportedMetric: 'Graphit can’t read how Rock splits this metric yet.',
}
/** @param {number} done @param {number} total */
export const addingProgress = (done, total) => `Adding ${done} of ${total}…`
/** @param {number} added @param {number} failed */
export const bulkSummary = (added, failed) =>
  failed > 0 ? `${added} added · ${failed} didn’t work` : `${added} added`
export const RETRY_FAILED = 'Retry the ones that didn’t work'
export const ROW_QUEUED = 'Waiting'
export const ROW_ADDING = 'Adding…'
export const ROW_ADDED = 'Added'
export const ROW_FAILED = 'Didn’t work'
export const ROW_ALREADY_EXISTS = 'Already in Graphit'
export const ROW_NEEDS_CAMPUSES = 'Match campuses first'
export const BULK_START_FAILED = 'Couldn’t start adding these. Try again.'
/** A setup item's failure when its own message isn't fit to show (25i). */
export const SETUP_ITEM_FAILED = 'Setup didn’t work for this one. We logged the details.'
export const NO_METRICS_TO_ADD = 'Nothing in your Rock to add yet'

export const MATCH_ALL_SERVICE_TIMES = 'Match all service times'
export const MATCHING_SERVICE_TIMES = 'Matching service times…'
export const MATCH_ALL_SERVICE_TIMES_HINT =
  'Graphit matches Rock service times to yours by name and adds any it doesn’t have yet. Retired ones are left out.'
export const MATCH_ALL_SERVICE_TIMES_FAILED = 'Couldn’t match your service times. Try again, or configure them manually.'
/**
 * "4 matched · 1 added · 1 left out (retired)" after "Match all service times".
 *
 * @param {{matched?: number, created?: number, skipped?: number, inactive?: number}} summary
 * @returns {string}
 */
export const matchAllServiceTimesSummary = ({ matched = 0, created = 0, skipped = 0, inactive = 0 } = {}) =>
  [
    `${matched} matched`,
    `${created} added`,
    skipped > 0 ? `${skipped} need a manual match` : null,
    inactive > 0 ? `${inactive} left out (retired)` : null,
  ]
    .filter(Boolean)
    .join(' · ')
export const SKIPPED_SERVICE_TIMES_HINT =
  'Some Rock service times could match more than one of yours, or have no usable name, so Graphit didn’t guess. Match them manually.'
export const CREATE_OPTIONS_LABEL = 'What Graphit creates in Rock'
/** @param {string} questions */
export const answerFirst = (questions) => `Answer ${questions} first.`

// --- T5: AI suggestions in the checklist ---------------------------------------

/** The badge on a row the AI suggested. */
export const SUGGESTED = 'Suggested'
/** The suggested column's badge with the server's `confidence_level`. */
export const SUGGESTED_BY_LEVEL = { high: 'Suggested · high', medium: 'Suggested · medium' }
/**
 * @param {string} measurement - the standard measurement a Rock metric is suggested for
 * @param {string} reason
 */
export const suggestedAs = (measurement, reason) => (reason ? `Suggested for ${measurement}: ${reason}` : `Suggested for ${measurement}`)

// --- S2: one standard measurement list, rules first (2026-09-30) -------------

/** Under the checklist header while the model still looks for less obvious matches (M9). */
export const PROVISIONAL_MATCHES = 'Graphit is still checking Rock for less obvious matches.'
export const PROVISIONAL_ADD_HINT = 'Wait a moment: Graphit is still checking Rock for more matches.'
/** @param {string} label - Rock's own category path, e.g. "Graphit" */
export const rockCategory = (label) => `Rock category “${label}”`
const NAMESAKE_DETAILS = {
  already_set_up: (title) => `Rock’s “${title}” is already in Graphit.`,
  skipped: (title) => `You skipped Rock’s “${title}” on Discover.`,
  unsupported_split: (title) => `Rock has “${title}”, but Graphit can’t read how it’s split yet.`,
  not_weekly: (title) => `Rock has “${title}”, but it isn’t calculated weekly.`,
}
/**
 * Why a standard measurement isn't offered for creating in Rock: Rock already
 * has a metric by that name (the board card's `rock_namesake`).
 *
 * @param {'already_set_up'|'skipped'|'unsupported_split'|'not_weekly'} why
 * @param {string} rockTitle
 */
export const namesakeDetail = (why, rockTitle) => (NAMESAKE_DETAILS[why] || NAMESAKE_DETAILS.unsupported_split)(rockTitle)
